import { useState, forwardRef, useImperativeHandle, useEffect } from 'react';
import { Box, List, Button, Grid2 as Grid, Typography, FormControlLabel, Checkbox, TextField } from '@mui/material';
import { AddCircleOutline as AddCircleOutlineIcon } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useDialog } from '@contexts/DialogContext';
import FieldListItem from './FieldListItem';
import FieldProperties from './FieldProperties';
import { langs as allLangs } from '@utils/helpers';

const FormIndex = forwardRef(({ initialData = '', setIsFormValid, customerId }, ref) => {
    const { t, i18n } = useTranslation();
    const { requestConfirm } = useDialog();

    const [formName, setFormName] = useState('');
    const [selectedLanguages, setSelectedLanguages] = useState([]);
    const [fields, setFields] = useState(null);
    const [selectedIndex, setSelectedIndex] = useState(null);

    const getData = () => {
        return {
            name: formName,
            languages: selectedLanguages,
            fields: fields
        }
    };

    useImperativeHandle(ref, () => ({
        getData: getData
    }));

    // Form valide si au moins une langue est sélectionnée et si au moins un champ est créé 
    useEffect(() => {
        setIsFormValid(formName.length > 0 && selectedLanguages.length > 0 && fields !== null && fields.length > 0);
    }, [formName, selectedLanguages, fields, setIsFormValid]);

    const handleLanguageChange = (language) => {
        setSelectedLanguages(prev => {
            if (prev.includes(language)) {
                return prev.filter(lang => lang !== language);
            }
            return [...prev, language];
        });
    };

    const getNewFieldName = () => {
        const isFieldNameTaken = (name) => fields.some(item => item.name === name);

        let counter = 1;
        let fieldName = 'field1';
        while (isFieldNameTaken(fieldName)) {
            counter++;
            fieldName = `field${counter}`;
        }

        return fieldName;
    };

    const handleAddField = () => {
        const newField = { name: getNewFieldName(), type: 'text', label: {}, helperText: {} };
        setFields([...fields, newField]);
        setSelectedIndex(fields.length);
    };

    const handleDeleteField = (index, event) => {
        event.stopPropagation();
        requestConfirm({
            message: t('Are you sure you want to delete this field?'),
            onConfirm: () => {
                const newFields = fields.filter((_, i) => i !== index);
                setFields(newFields);
                setSelectedIndex(null);
            }
        });
    };

    const handleFieldClick = (index) => {
        if (selectedIndex === index) {
            setSelectedIndex(null);
        } else {
            setSelectedIndex(index);
        }
    };

    const handleMoveField = (index, direction, event) => {
        event.stopPropagation();
        const newFields = [...fields];
        const temp = newFields[index];
        const newIndex = index + (direction === 'up' ? -1 : 1);
        newFields[index] = newFields[newIndex];
        newFields[newIndex] = temp;
        setFields(newFields);
        setSelectedIndex(newIndex);
    };

    const handleFieldUpdate = (updatedField) => {
        const newFields = [...fields];
        newFields[selectedIndex] = updatedField;
        setFields(newFields);
    };

    useEffect(() => {
        if (!initialData) {
            setFormName('');
            setSelectedLanguages([i18n.language]);
            setFields([]);
            return;
        }

        try {
            const parsedContent = JSON.parse(initialData);
            setFormName(parsedContent.name || '');
            setSelectedLanguages(parsedContent.languages);
            setFields(parsedContent.fields);
        } catch (error) {
            console.error('Error parsing initial content:', error);
        }
    }, [initialData, i18n.language]);

    return (
        <Grid container spacing={2}>
            <Grid size={12}>
                <Box sx={{ pt: 1 }}>
                    <TextField
                        fullWidth
                        label={t('Form name')}
                        value={formName}
                        onChange={(e) => setFormName(e.target.value)}
                        size="small"
                        required
                    />
                </Box>
            </Grid>
            <Grid size={12}>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ mr: 2 }}>{t('Languages')} :</Typography>
                    {Object.keys(allLangs).map((language) => (
                        <FormControlLabel
                            key={language}
                            control={
                                <Checkbox
                                    checked={selectedLanguages.includes(language)}
                                    onChange={() => handleLanguageChange(language)}
                                />
                            }
                            label={t(`lang.${language}`)}
                        />
                    ))}
                </Box>
            </Grid>
            <Grid size={6}>
                <Box>
                    {fields !== null && fields.length > 0 && (
                        <List sx={{
                            border: '1px solid',
                            borderColor: 'divider',
                            borderRadius: 2,
                            bgcolor: 'background.paper',
                            boxShadow: 1,
                            p: 0
                        }}>
                            {fields.map((item, index) => (
                                <FieldListItem
                                    key={index}
                                    field={item}
                                    selected={selectedIndex === index}
                                    onClick={() => handleFieldClick(index)}
                                    onDelete={(event) => handleDeleteField(index, event)}
                                    canMoveUp={index > 0}
                                    canMoveDown={index < fields.length - 1}
                                    onMoveUp={(event) => handleMoveField(index, 'up', event)}
                                    onMoveDown={(event) => handleMoveField(index, 'down', event)}
                                />
                            ))}
                        </List>
                    )}
                    <Button
                        variant="contained"
                        onClick={handleAddField}
                        color="primary"
                        sx={{
                            mt: 2,
                            textTransform: 'none'
                        }}
                        startIcon={<AddCircleOutlineIcon />}
                    >
                        {t('Add field')}
                    </Button>
                </Box>
            </Grid>
            <Grid size={6}>
                <Box sx={{
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                    bgcolor: 'background.paper',
                    boxShadow: 1,
                    p: 2,
                    minHeight: '200px'
                }}>
                    {selectedIndex !== null ? (
                        <FieldProperties
                            customerId={customerId}
                            field={fields[selectedIndex]}
                            onFieldUpdate={handleFieldUpdate}
                            availableLangs={selectedLanguages}
                        />
                    ) : (
                        fields === null || fields.length > 0 ? (
                            <Typography color="text.secondary" align="center" sx={{ mt: 8 }}>
                                {t('Select a field to configure its properties')}
                            </Typography>
                        ) : (
                            <Typography color="text.secondary" align="center" sx={{ mt: 8 }}>
                                {t('Click on "Add field" to create a field')}
                            </Typography>
                        )
                    )}
                </Box>
            </Grid>
        </Grid>
    );
});

export default FormIndex; 